"use client";

import { useCallback, useEffect, useState } from "react";

import { api } from "@/shared/lib/api";

interface MatchHostInfo {
  hostUserId: string | null;
  currentUserIsHost: boolean;
  /** KAN-14: состоит ли текущий пользователь в одной из команд этого матча. */
  currentUserIsParticipant: boolean;
  isLoading: boolean;
}

/**
 * Возвращает информацию о хосте матча — кто капитан Participant1, и является
 * ли текущий пользователь хостом. Используется чтобы скрывать кнопку «Создать
 * лобби» у всех, кроме хоста.
 *
 * Хост определяется на backend как капитан Participant1 (выше по сетке) —
 * принцип реальных турниров: лобби создаёт капитан верхней команды.
 *
 * GET /api/matches/{matchId}/players защищён CanViewMatchAsync — фетчится
 * только если юзер участник или админ.
 */
export function useMatchHostInfo(matchId: string | null | undefined): MatchHostInfo {
  const [hostUserId, setHostUserId] = useState<string | null>(null);
  const [currentUserIsHost, setCurrentUserIsHost] = useState(false);
  const [currentUserIsParticipant, setCurrentUserIsParticipant] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!matchId) return;
    setIsLoading(true);
    try {
      const resp = await api.get<{
        hostUserId?: string | null;
        currentUserIsHost?: boolean;
        currentUserIsParticipant?: boolean;
      }>(`/matches/${matchId}/players`);
      setHostUserId(resp.data?.hostUserId ?? null);
      setCurrentUserIsHost(Boolean(resp.data?.currentUserIsHost));
      setCurrentUserIsParticipant(Boolean(resp.data?.currentUserIsParticipant));
    } catch {
      // 403 (не участник) / 404 / 400 (матч не Live) — дефолты: не хост, не участник
      setHostUserId(null);
      setCurrentUserIsHost(false);
      setCurrentUserIsParticipant(false);
    } finally {
      setIsLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    void fetchInfo();
  }, [fetchInfo]);

  return { hostUserId, currentUserIsHost, currentUserIsParticipant, isLoading };
}
